import type { ParsedUrlQuery } from "node:querystring";
import type {
  IDateFilterUI,
  IFilterUI,
} from "@dinesapp/dines-ui/modules/reporting/types/Filters";
import {
  type CalculatedField,
  FilterType,
  type ReportField,
  ReportFieldType,
} from "@dinesapp/dines-ui/modules/reporting/types/report";

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export function toTitleCase(str: string): string {
  return str
    .split("_") // Split the string by underscores
    .map((word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()) // Capitalize each word
    .join(" "); // Join the words back with spaces
}

export function isString(value: unknown): value is string {
  return typeof value === "string";
}

export function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

export function isDate(value: unknown): value is Date {
  return value instanceof Date;
}

export const isDateFilter = (filter: IFilterUI): filter is IDateFilterUI => {
  return filter.filter_type === FilterType.Date;
};

export const isCalculatedField = (
  field: ReportField,
): field is CalculatedField => {
  return field.type === ReportFieldType.Calculated;
};

export const firstLetterToUpperCase = (string) =>
  string.replace(
    /(^\w|\s\w)(\S*)/g,
    (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase(),
  );

export const onSaveQueryToUrl = (filters, router, pageID = null) => {
  const queries = [];

  // biome-ignore lint/complexity/noForEach: <explanation>
  filters.forEach((filter) => {
    if (filter.conditional) {
      queries.push(filter.query);
    } else {
      queries.filter((query) => query !== filter.query);
    }
  });

  if (pageID) {
    if (queries.length !== 0) {
      router.replace(`${pageID}?${queries.join("&")}`, undefined, {
        shallow: true,
      });
    } else {
      router.replace(`${pageID}`, undefined, { shallow: true });
    }
    return;
  }

  if (queries.length !== 0) {
    router.replace(`?${queries.join("&")}`, undefined, {
      shallow: true,
    });
  } else {
    router.replace("", undefined, { shallow: true });
  }
};

export const getCSVFileName = (url: string) => {
  const parsed = new URL(url);
  return decodeURIComponent(parsed.pathname.split("/").pop() || "file.csv");
};

/**
 * Resolve a URL query parameter to a value from a string-keyed map.
 * Returns the matched value when `query[param]` is a string present in
 * `map`; otherwise returns `fallback`. Useful for seeding view-model
 * state from a query like `?section=categories`.
 */
export const resolveEnumFromQuery = <TKey extends string, TValue>(
  query: ParsedUrlQuery,
  param: string,
  map: Record<TKey, TValue>,
  fallback: TValue,
): TValue => {
  const raw = query[param];
  if (
    typeof raw === "string" &&
    Object.prototype.hasOwnProperty.call(map, raw)
  ) {
    return map[raw as TKey];
  }
  return fallback;
};
